/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Modal that displays a single proposal's details (description, quote, cost, company info)
 * and allows approve/reject/star actions. Company data is profile-aware (perfilSeleccionado 0/1/2).
 */

import React, { useRef, useEffect, useState, useCallback } from "react";
import styles from "./ProposalDisplayModal.module.css";
import { useNavigate } from "react-router";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { handleRejectSend } from "../../logic/proposal/index.js";
import RejectProposalModal from "../RejectProposalModal/index.jsx";
import {
  getCompanyFieldValue,
  PROFILE_EDITABLE_FIELDS,
} from "../../utils/common.js";
import { useTranslation } from "react-i18next";
import ProposalDetailsContent from "../../components/ProposalDetailsContent";
import { useProposalReview } from "../../components/ProposalDetailsContent/useProposalReview";

/** Opens and closes the native dialog, and dismisses this modal's toast when it closes. */
const useProposalDialog = (open, onClose) => {
  const dialogRef = useRef(null);
  const toastId = useRef(null);
  const handleDismissAndClose = useCallback(() => {
    toast.dismiss(toastId.current);
    onClose();
  }, [onClose]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleDismissAndClose);
    }

    if (open) {
      dialog.showModal();
    } else {
      dialog.close();
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleDismissAndClose);
      }
    };
  }, [handleDismissAndClose, open]);

  return { dialogRef, handleDismissAndClose };
};

const mapProfileCompany = (company, perfilSeleccionado) => {
  if (!company) {
    return null;
  }
  const merged = { ...company };
  PROFILE_EDITABLE_FIELDS.forEach((key) => {
    merged[key] = getCompanyFieldValue(company, key, perfilSeleccionado, "");
  });
  return merged;
};

/**
 * Modal for viewing a proposal's full details and performing approve, reject, or star actions.
 * Company fields follow perfilSeleccionado: 0 is the root company, 1 or 2 come from perfiles.
 */
const ProposalDisplayModal = ({ open, onClose, proposal, requirement }) => {
  const navigate = useNavigate();
  const { dialogRef, handleDismissAndClose } = useProposalDialog(open, onClose);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const { t } = useTranslation();
  const openRejectModal = useCallback(() => {
    setShowRejectModal(true);
  }, []);
  const perfilSeleccionado = proposal?.perfilSeleccionado ?? 0;
  const mapCompany = useCallback(
    (company) => mapProfileCompany(company, perfilSeleccionado),
    [perfilSeleccionado],
  );
  const review = useProposalReview({
    proposal,
    navigate,
    onComplete: onClose,
    userId: proposal?.idUsuario,
    requireUserId: true,
    onReject: openRejectModal,
    mapCompany,
  });

  /**
   * Sends the rejection notification and closes the modal.
   * @param {string} proposalId - The proposal _id.
   * @param {string} rejectionReason - Reason text for the rejection.
   * @returns {Promise<void>}
   */
  const onRejectSend = useCallback(async (proposalId, rejectionReason) => {
    await handleRejectSend(proposalId, rejectionReason, navigate, onClose);
  }, [navigate, onClose]);

  const handleCloseRejectModal = useCallback(() => {
    setShowRejectModal(false);
  }, []);

  const handleRejectModalSend = useCallback(
    (proposalId, rejectionReason) => {
      onRejectSend(proposalId, rejectionReason);
      onClose();
    },
    [onClose, onRejectSend],
  );

  return (
    <>
      <div className={styles.modalOverlay}>
        <dialog ref={dialogRef} className={styles.modal}>
          <div className={styles.modalHeader}>
            <h2>
              {t("components.proposalDisplayModal.title")}:{" "}
              {requirement?.titulo}
            </h2>
            <button
              className={styles.closeButton}
              onClick={handleDismissAndClose}
            >
              ×
            </button>
          </div>

          <div className={styles.modalContent}>
            <ProposalDetailsContent
              styles={styles}
              proposal={proposal}
              {...review}
            />
          </div>
        </dialog>
      </div>
      <RejectProposalModal
        open={showRejectModal}
        onClose={handleCloseRejectModal}
        onSend={handleRejectModalSend}
        proposal={proposal}
        isStarred={review.isStarred}
      />
      <ToastContainer />
    </>
  );
};

export default ProposalDisplayModal;
